import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import { Eyebrow, Fleuron, HeadingItalic } from "../../components/design";
import { CallerIDNoticeInline } from "../../components/design/CallerIDNotice";
import {
  adminDeleteCall,
  adminDeleteUser,
  adminDownloadBookPdf,
  adminGetUser,
  adminListCalls,
  adminListChapters,
  adminResendWelcome,
  adminUpdateUser,
  type AdminCall,
  type AdminUserDoc,
  type ChapterSummary,
} from "../../lib/api";
import { formatTimestamp } from "../../lib/format";

const EDITABLE_FIELDS = [
  "interviewee_first_name",
  "interviewee_last_name",
  "orderer_first_name",
  "orderer_last_name",
  "orderer_email",
  "target_group",
  "person_info",
  "life_events",
  "stammdaten",
  "sensitivities",
  "chapters_to_exclude",
  "language",
  "aliases",
] as const;

type EditState = Record<string, any>;

function fieldLabel(key: string): string {
  const map: Record<string, string> = {
    interviewee_first_name: "Erzähler – Vorname",
    interviewee_last_name: "Erzähler – Nachname",
    orderer_first_name: "Auftraggeber – Vorname",
    orderer_last_name: "Auftraggeber – Nachname",
    orderer_email: "Auftraggeber – E-Mail",
    target_group: "Zielgruppe (Komma-getrennt)",
    person_info: "Erste Angaben zur Person",
    life_events: "Meilensteine & Lebensereignisse",
    stammdaten: "Stammdaten (JSON)",
    sensitivities: "Sensibilitäten / Tabu-Themen",
    chapters_to_exclude: "Kapitel ausschließen",
    language: "Sprache (z. B. DE)",
    aliases: "Alternative Telefonnummern (Komma-getrennt)",
  };
  return map[key] || key;
}

function isLongText(key: string): boolean {
  return [
    "person_info",
    "life_events",
    "sensitivities",
    "chapters_to_exclude",
    "stammdaten",
  ].includes(key);
}

function valueToEditable(key: string, value: any): string {
  if (value === undefined || value === null) return "";
  if (key === "target_group" || key === "aliases") {
    return Array.isArray(value) ? value.join(", ") : String(value);
  }
  if (key === "stammdaten") {
    try {
      return JSON.stringify(value || {}, null, 2);
    } catch {
      return "";
    }
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function editableToValue(key: string, raw: string): any {
  if (key === "target_group" || key === "aliases") {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (key === "stammdaten") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    return JSON.parse(trimmed); // throws if invalid
  }
  return raw;
}

export default function AdminUserDetail() {
  const { phone: phoneParam = "" } = useParams();
  const phone = decodeURIComponent(phoneParam);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<AdminUserDoc | null>(null);
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [calls, setCalls] = useState<AdminCall[]>([]);

  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditState>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [d, c, t] = await Promise.all([
        adminGetUser(phone),
        adminListChapters(phone).catch(() => ({ chapters: [] })),
        adminListCalls(phone).catch(() => ({ calls: [] })),
      ]);
      setDoc(d);
      setChapters(c.chapters || []);
      setCalls(t.calls || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  function startEdit() {
    if (!doc) return;
    const initial: EditState = {};
    for (const f of EDITABLE_FIELDS) {
      initial[f] = valueToEditable(f, doc.data?.[f]);
    }
    setEditValues(initial);
    setSaveMessage(null);
    setEditing(true);
  }

  async function saveEdit() {
    setSaving(true);
    setSaveMessage(null);
    try {
      const patch: Record<string, any> = {};
      for (const f of EDITABLE_FIELDS) {
        const original = valueToEditable(f, doc?.data?.[f]);
        const current = editValues[f] ?? "";
        if (original === current) continue;
        try {
          patch[f] = editableToValue(f, current);
        } catch (e: any) {
          throw new Error(
            `Feld "${fieldLabel(f)}" ist kein gültiges JSON: ${e?.message || e}`
          );
        }
      }
      if (Object.keys(patch).length === 0) {
        setSaveMessage("Keine Änderungen.");
        setEditing(false);
        return;
      }
      await adminUpdateUser(phone, patch);
      setSaveMessage("Gespeichert ✓");
      setEditing(false);
      await loadAll();
    } catch (e: any) {
      setSaveMessage(e?.message || "Fehler beim Speichern.");
    } finally {
      setSaving(false);
    }
  }

  async function handleResendWelcome() {
    if (!window.confirm("Welcome-Mail erneut versenden?")) return;
    setResendBusy(true);
    setResendMessage(null);
    try {
      await adminResendWelcome(phone);
      setResendMessage(
        "Welcome-Mail wird gesendet. Es kann eine Minute dauern, bis sie ankommt."
      );
      await loadAll();
    } catch (e: any) {
      setResendMessage(e?.message || "Fehler beim Versand.");
    } finally {
      setResendBusy(false);
    }
  }

  async function handleDeleteUser() {
    if (
      !window.confirm(
        `Erzähler ${phone} mit allen Kapiteln und Anrufen UNWIDERRUFLICH löschen?`
      )
    ) {
      return;
    }
    const confirmName = window.prompt(
      'Zum Bestätigen "LÖSCHEN" eintippen:'
    );
    if (confirmName !== "LÖSCHEN") return;

    setDeletingUser(true);
    try {
      await adminDeleteUser(phone);
      navigate("/admin", { replace: true });
    } catch (e: any) {
      alert(e?.message || "Fehler beim Löschen.");
      setDeletingUser(false);
    }
  }

  async function handleDeleteCall(callId: string) {
    if (!window.confirm("Diesen Anruf-Datensatz löschen?")) return;
    try {
      await adminDeleteCall(phone, callId);
      setCalls((c) => c.filter((x) => x.transcriptId !== callId));
    } catch (e: any) {
      alert(e?.message || "Fehler beim Löschen.");
    }
  }

  if (loading) return <FullPageLoading />;

  const d = doc?.data || {};
  const intervieweeName = `${d.interviewee_first_name || ""} ${
    d.interviewee_last_name || ""
  }`.trim();

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />
      <main
        className="desk-container-admin"
        style={{
          padding: "32px 22px 60px",
        }}
      >
        <Link
          to="/admin"
          style={{
            fontFamily: "var(--f-ui)",
            fontSize: 13,
            color: "var(--ink-faint)",
            textDecoration: "none",
          }}
        >
          ← Zurück zur Übersicht
        </Link>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 16,
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "12px 16px",
              color: "var(--oxblood-d)",
            }}
          >
            {error}
          </div>
        )}

        <header
          style={{
            marginTop: 18,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div>
            <Eyebrow>Admin · Erzähler</Eyebrow>
            <HeadingItalic size={32}>
              <em>{intervieweeName || "Unbenannt"}</em>
            </HeadingItalic>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontSize: 14,
                color: "var(--ink-faint)",
                margin: "2px 0 0",
              }}
            >
              {phone}
            </p>
          </div>
          {!editing && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link
                to={`/admin/users/${encodeURIComponent(phone)}/preview`}
                className="btn-secondary"
              >
                Kundenansicht
              </Link>
              <BookPdfButton phone={phone} />
              <button onClick={startEdit} className="btn-secondary">
                Bearbeiten
              </button>
            </div>
          )}
        </header>

        <Fleuron style={{ margin: "16px 0 22px" }} />

        <div className="desk-admin-detail-grid">
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Stammdaten / editable fields */}
          <section
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 14,
              padding: "20px 22px",
              boxShadow:
                "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: 22,
                  margin: 0,
                  color: "var(--ink)",
                }}
              >
                Stammdaten
              </h2>
              {saveMessage && (
                <span
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 13,
                    color: "var(--ink-soft)",
                  }}
                >
                  {saveMessage}
                </span>
              )}
            </div>

            {editing ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {EDITABLE_FIELDS.map((f) => (
                  <div key={f}>
                    <label
                      className="t-caps"
                      htmlFor={`f-${f}`}
                      style={{ display: "block", marginBottom: 6 }}
                    >
                      {fieldLabel(f)}
                    </label>
                    {isLongText(f) ? (
                      <textarea
                        id={`f-${f}`}
                        className="input"
                        style={{
                          minHeight: 100,
                          fontFamily:
                            f === "stammdaten"
                              ? "ui-monospace, Menlo, monospace"
                              : "var(--f-body)",
                          fontSize: f === "stammdaten" ? 13 : 16,
                        }}
                        value={editValues[f] ?? ""}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, [f]: e.target.value }))
                        }
                      />
                    ) : (
                      <input
                        id={`f-${f}`}
                        className="input"
                        value={editValues[f] ?? ""}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, [f]: e.target.value }))
                        }
                      />
                    )}
                    {f === "aliases" && (
                      <div style={{ marginTop: 6 }}>
                        <CallerIDNoticeInline>
                          Alternative Nummern werden ebenfalls per Absender-ID
                          erkannt.
                        </CallerIDNoticeInline>
                      </div>
                    )}
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    paddingTop: 6,
                  }}
                >
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? <InlineSpinner /> : "Speichern"}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setSaveMessage(null);
                    }}
                    disabled={saving}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <dl
                data-grid-2
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  rowGap: 14,
                  columnGap: 22,
                  margin: 0,
                }}
              >
                {EDITABLE_FIELDS.map((f) => {
                  const value = d[f];
                  let display: string;
                  if (value === undefined || value === null || value === "") {
                    display = "—";
                  } else if (Array.isArray(value)) {
                    display = value.length ? value.join(", ") : "—";
                  } else if (typeof value === "object") {
                    display = JSON.stringify(value, null, 2);
                  } else {
                    display = String(value);
                  }
                  return (
                    <div key={f} style={{ minWidth: 0 }}>
                      <dt
                        className="t-caps"
                        style={{ marginBottom: 4 }}
                      >
                        {fieldLabel(f)}
                      </dt>
                      <dd
                        style={{
                          fontFamily: "var(--f-body)",
                          fontSize: 15,
                          color: "var(--ink)",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {display}
                      </dd>
                    </div>
                  );
                })}
                <div style={{ gridColumn: "1 / -1" }}>
                  <dt className="t-caps" style={{ marginBottom: 4 }}>
                    Erstellt
                  </dt>
                  <dd
                    style={{
                      fontFamily: "var(--f-body)",
                      fontSize: 15,
                      color: "var(--ink)",
                      margin: 0,
                    }}
                  >
                    {formatTimestamp(d.createdAt)}
                  </dd>
                </div>
              </dl>
            )}
          </section>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Calls */}
          <ListSection
            title={`Anrufe (${calls.length})`}
            empty="Noch keine Anrufe."
            isEmpty={calls.length === 0}
          >
            {calls.map((c, i) => (
              <li
                key={c.transcriptId}
                style={{
                  padding: "14px 0",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                  borderTop:
                    i === 0 ? "none" : "1px solid rgba(43,26,15,0.08)",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 15,
                      margin: 0,
                      color: "var(--ink)",
                    }}
                  >
                    {formatTimestamp(c.timestamp)}{" "}
                    <span
                      style={{
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontSize: 11,
                        color: "var(--ink-faint)",
                        marginLeft: 6,
                      }}
                    >
                      {c.transcriptId}
                    </span>
                  </p>
                  {c.preview && (
                    <p
                      style={{
                        fontFamily: "var(--f-body)",
                        fontStyle: "italic",
                        fontSize: 13.5,
                        color: "var(--ink-faint)",
                        margin: "4px 0 0",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {c.preview}
                    </p>
                  )}
                  <p
                    style={{
                      fontFamily: "var(--f-ui)",
                      fontSize: 11,
                      color: "var(--ink-faint)",
                      margin: "4px 0 0",
                    }}
                  >
                    {c.length} Zeichen ·{" "}
                    {c.processed ? "verarbeitet" : "offen"}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteCall(c.transcriptId)}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "var(--f-ui)",
                    fontSize: 13,
                    color: "var(--oxblood)",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(107,32,24,0.4)",
                    textUnderlineOffset: 3,
                    flexShrink: 0,
                  }}
                >
                  Löschen
                </button>
              </li>
            ))}
          </ListSection>

          {/* Chapters */}
          <ListSection
            title={`Kapitel (${chapters.length})`}
            empty="Noch keine Kapitel."
            isEmpty={chapters.length === 0}
          >
            {chapters.map((c, i) => (
              <li
                key={c.title}
                style={{
                  padding: "14px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  borderTop:
                    i === 0 ? "none" : "1px solid rgba(43,26,15,0.08)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 15,
                      margin: 0,
                      color: "var(--ink)",
                    }}
                  >
                    {c.title}
                  </p>
                  {c.summary && (
                    <p
                      style={{
                        fontFamily: "var(--f-body)",
                        fontStyle: "italic",
                        fontSize: 13.5,
                        color: "var(--ink-faint)",
                        margin: "4px 0 0",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {c.summary}
                    </p>
                  )}
                  <p
                    style={{
                      fontFamily: "var(--f-ui)",
                      fontSize: 11,
                      color: "var(--ink-faint)",
                      margin: "4px 0 0",
                    }}
                  >
                    {c.content_length} Zeichen · Sättigung{" "}
                    {c.saturation_score}%
                  </p>
                </div>
                <Link
                  to={`/admin/users/${encodeURIComponent(phone)}/chapters/${encodeURIComponent(c.title)}`}
                  style={{
                    fontFamily: "var(--f-ui)",
                    fontSize: 13,
                    color: "var(--oxblood)",
                    textDecoration: "none",
                    flexShrink: 0,
                  }}
                >
                  Öffnen →
                </Link>
              </li>
            ))}
          </ListSection>
          </div>
        </div>

        <div style={{ marginTop: 22 }}>
          {/* Danger zone */}
          <section
            className="paper-deep"
            style={{
              borderRadius: 14,
              padding: "22px 22px 20px",
              boxShadow:
                "0 1px 0 rgba(0,0,0,0.18), 0 14px 28px -10px rgba(74,20,14,0.45)",
            }}
          >
            <div
              className="t-caps"
              style={{ color: "rgba(244,236,216,0.55)", marginBottom: 6 }}
            >
              Gefährliche Aktionen
            </div>
            <h2
              style={{
                fontFamily: "var(--f-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 24,
                margin: "0 0 8px",
                color: "var(--paper-50)",
              }}
            >
              Mit Bedacht.
            </h2>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontSize: 14.5,
                color: "rgba(244,236,216,0.78)",
                margin: "0 0 18px",
              }}
            >
              Diese Aktionen können nicht rückgängig gemacht werden.
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <button
                onClick={handleResendWelcome}
                disabled={resendBusy}
                style={ghostCreamPill}
              >
                {resendBusy ? <InlineSpinner /> : "Welcome-Mail erneut senden"}
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deletingUser}
                style={{
                  background: "var(--oxblood-d)",
                  color: "var(--paper-50)",
                  border: "1px solid rgba(244,236,216,0.30)",
                  borderRadius: 999,
                  padding: "11px 20px",
                  fontFamily: "var(--f-ui)",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: "0 2px 0 rgba(0,0,0,0.30)",
                  opacity: deletingUser ? 0.6 : 1,
                }}
              >
                {deletingUser ? <InlineSpinner /> : "Erzähler komplett löschen"}
              </button>
            </div>
            {resendMessage && (
              <p
                style={{
                  marginTop: 14,
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13.5,
                  color: "rgba(244,236,216,0.85)",
                }}
              >
                {resendMessage}
              </p>
            )}
          </section>
        </div>
      </main>
      <style>{`
        @media (min-width: 720px) {
          [data-grid-2] { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const ghostCreamPill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: "transparent",
  color: "var(--paper-50)",
  fontFamily: "var(--f-ui)",
  fontWeight: 500,
  fontSize: 14,
  padding: "10px 16px",
  borderRadius: 999,
  border: "1px solid rgba(244,236,216,0.45)",
  cursor: "pointer",
  textDecoration: "none",
};

function ListSection({
  title,
  isEmpty,
  empty,
  children,
}: {
  title: string;
  isEmpty: boolean;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 14,
        padding: "18px 22px 12px",
        boxShadow:
          "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
      }}
    >
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 22,
          margin: "0 0 10px",
          color: "var(--ink)",
        }}
      >
        {title}
      </h2>
      {isEmpty ? (
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            color: "var(--ink-faint)",
            margin: "4px 0 12px",
          }}
        >
          {empty}
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {children}
        </ul>
      )}
    </section>
  );
}

function BookPdfButton({ phone }: { phone: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setError(null);
    setBusy(true);
    try {
      await adminDownloadBookPdf(phone);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download fehlgeschlagen.");
      setTimeout(() => setError(null), 6000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="btn-secondary"
        title="Buch als PDF aus Google Doc exportieren"
      >
        {busy ? <InlineSpinner /> : "Buch-PDF"}
      </button>
      {error && (
        <span
          role="alert"
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 11,
            color: "var(--error)",
            maxWidth: 220,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}
